import { useState, useMemo, useCallback, type ReactNode } from "react";
import { useCanEdit } from "@/lib/documentAccess";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/lilia-ui/dialog";
import { Button } from "@/components/lilia-ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/lilia-ui/tabs";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/lilia-ui/dropdown-menu";
import { IconChevronDown, IconTrash } from "@tabler/icons-react";
import { latexLanguage } from "@/lib/codemirror/latex-language";
import { SourceCodeViewer } from "./preview/SourceCodeViewer";
import { cleanImageUrls, stripImages } from "@/lib/latex-image-utils";
import { useTranslation } from "@/utils/hooks/useTranslation";
import type { JSONContent } from "@tiptap/core";
import { useApiContext } from "@/contexts/ApiContext";
import { blockLatex } from "@/lib/blockLatex";

/**
 * Optional "Paste LaTeX" affordance. When provided, BlockEditModal adds a
 * third tab with a textarea + parse button. On successful parse it hands the
 * caller a single parsed block of the expected type (e.g. "table" for
 * TableBlock) so the caller can populate its draft state from it.
 */
export interface LatexPasteConfig {
  /** Block type the caller wants to extract (e.g. "table", "figure"). */
  expectedType: string;
  /** Called on successful parse with the first matching block's content. */
  onImport: (content: Record<string, unknown>) => void;
  /** Authenticated fetch for POST /api/convert/latex-to-blocks. */
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>;
  /** Optional placeholder for the textarea. */
  placeholder?: string;
}

interface BlockEditModalProps {
  title: string;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  onDelete?: () => void;
  width?: number;
  /** Use `width` as is (capped to the window) instead of growing to 80vw. */
  fixedWidth?: boolean;
  /** Fill the window as a full-height sheet: Cancel, title, Save in one header row. */
  sheet?: boolean;
  children: ReactNode;
  showLatexTab?: boolean;
  latexContent?: string;
  latexPaste?: LatexPasteConfig;
  /** Close the save path while committing would destroy data the editor cannot
   *  yet represent. The reason is shown to the author, never only in a tooltip. */
  saveDisabled?: boolean;
  saveDisabledReason?: string;
  /** The canvas node this modal shows. A viewer gets "Copy LaTeX" for it
   *  (view-only handoff): the LaTeX Lilia compiles for the block. */
  sourceNode?: { toJSON(): JSONContent };
}

export function BlockEditModal({
  title,
  open,
  onClose,
  onSave,
  onDelete,
  width = 720,
  fixedWidth = false,
  sheet = false,
  children,
  showLatexTab = false,
  latexContent,
  latexPaste,
  saveDisabled = false,
  saveDisabledReason,
  sourceNode,
}: BlockEditModalProps) {
  // A viewer may open a block to read it, but not save or delete it.
  const readOnly = !useCanEdit();
  const { t } = useTranslation('editor');
  const [activeTab, setActiveTab] = useState("edit");
  const [copied, setCopied] = useState<string | false>(false);
  const { fetchWithAuth } = useApiContext();
  const [copyState, setCopyState] = useState<"idle" | "copying" | "copied" | "failed">("idle");
  const copyBlockLatex = useCallback(async () => {
    if (!sourceNode) return;
    setCopyState("copying");
    try {
      await navigator.clipboard.writeText(await blockLatex(fetchWithAuth, sourceNode.toJSON()));
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
    setTimeout(() => setCopyState("idle"), 2000);
  }, [fetchWithAuth, sourceNode]);
  const [pasteInput, setPasteInput] = useState("");
  const [pasteStatus, setPasteStatus] = useState<"idle" | "parsing" | "error">("idle");
  const [pasteError, setPasteError] = useState<string | null>(null);

  const handleParseLatex = useCallback(async () => {
    if (!latexPaste || !pasteInput.trim()) return;
    setPasteStatus("parsing");
    setPasteError(null);
    try {
      const response = await latexPaste.fetchWithAuth("/api/convert/latex-to-blocks", {
        method: "POST",
        body: JSON.stringify({ latex: pasteInput }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({ message: "Failed to parse LaTeX" }));
        throw new Error(data.message || "Failed to parse LaTeX");
      }
      const data = (await response.json()) as {
        blocks: { type: string; content: Record<string, unknown> }[];
      };
      const match = data.blocks.find((b) => b.type === latexPaste.expectedType);
      if (!match) {
        throw new Error(`No ${latexPaste.expectedType} block found in the pasted LaTeX.`);
      }
      latexPaste.onImport(match.content);
      setPasteInput("");
      setPasteStatus("idle");
      setActiveTab("edit");
    } catch (err) {
      setPasteStatus("error");
      setPasteError(err instanceof Error ? err.message : "Unknown error");
    }
  }, [latexPaste, pasteInput]);

  const rawLatex = latexContent || "";
  const { cleaned: displayLatex, mappings } = useMemo(
    () => cleanImageUrls(rawLatex),
    [rawLatex],
  );
  const hasImages = mappings.length > 0;

  const showCopiedFeedback = useCallback((label: string) => {
    setCopied(label);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  const handleCopyLatex = useCallback(() => {
    if (displayLatex) {
      navigator.clipboard.writeText(displayLatex);
      showCopiedFeedback(t('copied'));
    }
  }, [displayLatex, showCopiedFeedback, t]);

  const handleCopyWithUrls = useCallback(() => {
    if (rawLatex) {
      navigator.clipboard.writeText(rawLatex);
      showCopiedFeedback(t('copied'));
    }
  }, [rawLatex, showCopiedFeedback, t]);

  const handleCopyWithoutImages = useCallback(() => {
    if (displayLatex) {
      navigator.clipboard.writeText(stripImages(displayLatex));
      showCopiedFeedback(t('copied'));
    }
  }, [displayLatex, showCopiedFeedback, t]);

  // Sizing: formulas and tables need real estate. Pre-launch (2026-04-24)
  // the modal was capped at `width` (720 default) which cramped long
  // equations and wide tables. Now: min(80vw, 1400px) so large screens
  // get breathing room while small screens (still desktop — flow is
  // disabled on mobile) stay within the viewport. Callers can still pass
  // a larger `width` to override the 1400px cap.
  const desiredMaxWidth = fixedWidth
    ? `min(${width}px, calc(100vw - 24px))`
    : `min(80vw, ${Math.max(width, 1400)}px)`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="block-edit-modal"
        data-sheet={sheet ? "true" : undefined}
        className={
          sheet
            ? "!left-0 !top-0 !h-[100dvh] !max-h-none !max-w-none !w-screen !translate-x-0 !translate-y-0 !rounded-none !p-3"
            : fixedWidth
              ? ""
              : "max-w-[80vw]"
        }
        style={sheet ? undefined : { maxWidth: desiredMaxWidth, width: desiredMaxWidth }}
        // Avoid focus bouncing back into TipTap eating the close/save click.
        onCloseAutoFocus={(e) => e.preventDefault()}
        // Take focus into the modal itself (not its first field): left in
        // the document, focus kept the block's node selection, and the first
        // key typed replaced the block — opening an equation and typing "a"
        // deleted the equation.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus({ preventScroll: true });
        }}
      >
        <DialogHeader>
          {/* Actions live in the header (top), not a bottom footer — founder
              direction 2026-06-14, applied across all block modals. pr-10
              leaves room for the Dialog's built-in × close (top-4 right-4). */}
          <div className="flex items-center justify-between gap-3 w-full pr-10">
            {sheet && (
              <Button variant="outline" size="sm" onClick={onClose} data-testid="block-edit-modal-cancel" className="min-h-11">
                {t('cancel', { ns: 'common' })}
              </Button>
            )}
            <DialogTitle>{title}</DialogTitle>
            <div className="flex items-center gap-2">
              {readOnly && (
                <span data-testid="block-edit-modal-view-only" className="text-xs text-[var(--lilia-text-muted)]">View only</span>
              )}
              {readOnly && sourceNode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyBlockLatex}
                  disabled={copyState === "copying"}
                  data-testid="block-edit-modal-copy-latex"
                  data-state={copyState}
                >
                  {copyState === "copied" ? t('copied') : copyState === "failed" ? "Couldn't copy" : "Copy LaTeX"}
                </Button>
              )}
              {onDelete && !readOnly && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { if (window.confirm("Delete this block?")) onDelete(); }}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  data-testid="block-edit-modal-delete"
                >
                  <IconTrash size={14} className="mr-1" /> Delete
                </Button>
              )}
              {!sheet && (
                <Button variant="outline" size="sm" onClick={onClose} data-testid="block-edit-modal-cancel">
                  {t('cancel', { ns: 'common' })}
                </Button>
              )}
              {!readOnly && (
              <Button
                variant="primary"
                size="sm"
                onClick={onSave}
                disabled={saveDisabled}
                title={saveDisabled ? saveDisabledReason : undefined}
                data-testid="block-edit-modal-save"
              >
                {t('save', { ns: 'common' })}
              </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 w-full flex-col">
          {showLatexTab ? (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="edit">{t('editTab')}</TabsTrigger>
                <TabsTrigger value="latex">{t('latexPreview')}</TabsTrigger>
                {latexPaste && <TabsTrigger value="paste">Paste LaTeX</TabsTrigger>}
              </TabsList>
              <TabsContent value="edit">
                <div className="py-4 w-full flex-1 flex flex-col min-h-0">
                  {children}
                </div>
              </TabsContent>
              <TabsContent value="latex">
                <div className="py-4 flex-1 flex flex-col min-h-0 w-full">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--lilia-text-muted)]">{t('generatedLatex')}</span>
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCopyLatex}
                      >
                        {copied || t('copy')}
                      </Button>
                      {hasImages && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-xs" title={t('copyOptions')}>
                              <IconChevronDown size={14} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="z-[10100]">
                            <DropdownMenuItem onClick={handleCopyLatex}>
                              {t('copyCleanPaths')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleCopyWithUrls}>
                              {t('copyWithImageUrls')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleCopyWithoutImages}>
                              {t('copyWithoutImages')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                  <SourceCodeViewer
                    value={displayLatex || t('noLatexContent', '% No LaTeX content')}
                    language={latexLanguage}
                  />
                </div>
              </TabsContent>
              {latexPaste && (
                <TabsContent value="paste">
                  <div className="py-4 flex-1 flex flex-col min-h-0 w-full gap-3">
                    <p className="text-[12px] text-[var(--lilia-text-muted)]">
                      Paste raw LaTeX source (e.g. <code>\begin{`{${latexPaste.expectedType === "table" ? "table" : latexPaste.expectedType}}`}...\end{`{${latexPaste.expectedType === "table" ? "table" : latexPaste.expectedType}}`}</code>). We&apos;ll parse it and replace this block&apos;s content.
                    </p>
                    <textarea
                      value={pasteInput}
                      onChange={(e) => {
                        setPasteInput(e.target.value);
                        if (pasteStatus === "error") {
                          setPasteStatus("idle");
                          setPasteError(null);
                        }
                      }}
                      placeholder={latexPaste.placeholder ?? "\\begin{...}\n...\n\\end{...}"}
                      rows={12}
                      className="w-full rounded-lg border border-[var(--lilia-border)] bg-[var(--lilia-bg)] p-3 font-mono text-sm text-[var(--lilia-text)] placeholder:text-[var(--lilia-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--lilia-ring)] resize-y"
                      disabled={pasteStatus === "parsing"}
                    />
                    <div className="flex items-center gap-3">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleParseLatex}
                        disabled={!pasteInput.trim() || pasteStatus === "parsing"}
                      >
                        {pasteStatus === "parsing" ? "Parsing…" : "Parse & replace"}
                      </Button>
                      {pasteError && (
                        <span className="text-[12px] text-red-500">{pasteError}</span>
                      )}
                    </div>
                  </div>
                </TabsContent>
              )}
            </Tabs>
          ) : (
            <div className="py-4 w-full flex-1 flex flex-col min-h-0">
              {children}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
